import {
  BarChart3,
  Users,
  Star,
  TrendingUp,
  Scale,
  FileText,
  LucideIcon,
} from 'lucide-react'

export interface Product {
  id: string
  name: string
  tagline: string
  description: string
  icon: LucideIcon
  color: 'indigo' | 'teal' | 'amber' | 'emerald' | 'violet' | 'rose'
  logoUrl?: string
  screenshotUrl?: string
  features: string[]
  problemsSolved: string[]
  targetAudience: string[]
  faqs: { question: string; answer: string }[]
}

export const products: Product[] = [
  {
    id: 'budget-navigator',
    name: 'Budget Navigator',
    tagline: 'Financial planning & budget control for SMEs.',
    description:
      'Take control of your company finances with real-time budget tracking, forecasting, and expense management. Designed specifically for growing teams that need clarity without the complexity of enterprise ERPs.',
    icon: BarChart3,
    color: 'indigo',
    features: [
      'Real-time budget tracking',
      'Automated expense categorization',
      'Cash flow forecasting',
      'Departmental budget allocation',
      'Variance analysis reports',
      'Multi-currency support',
      'Integration with major banks',
      'Customizable financial dashboards',
    ],
    problemsSolved: [
      'Lack of visibility into real-time spending',
      'Manual spreadsheet errors and version control issues',
      'Difficulty forecasting cash flow accurately',
      'Slow approval processes for expenses',
      'Disconnected financial data across departments',
    ],
    targetAudience: [
      'CFOs and Finance Directors',
      'Small Business Owners',
      'Department Heads',
      'Accountants',
    ],
    faqs: [
      {
        question: 'Does it integrate with Xero or QuickBooks?',
        answer:
          'Yes, Budget Navigator offers seamless two-way synchronization with Xero, QuickBooks Online, and Sage Intacct.',
      },
      {
        question: 'Can I set different permission levels?',
        answer:
          'Absolutely. You can define granular permissions for admins, department heads, and regular employees to ensure data security.',
      },
      {
        question: 'Is historical data import supported?',
        answer:
          'Yes, we provide tools to easily import up to 5 years of historical financial data via CSV or direct API connection.',
      },
      {
        question: 'How secure is my financial data?',
        answer:
          'We use bank-grade 256-bit encryption and are SOC 2 Type II compliant. Your data is backed up daily and stored in redundant data centers.',
      },
    ],
  },
  {
    id: 'cooler-pulse',
    name: 'Cooler Pulse',
    tagline: 'HR analytics & employee engagement insights.',
    description:
      'Understand your team better with automated pulse surveys, retention analytics, and culture health monitoring. Build a workplace where top talent wants to stay and grow.',
    icon: Users,
    color: 'teal',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/ti5vBa5Jt36b9sWU3ZSHuS/Logo_CoolerPulse.png',
    screenshotUrl:
      'https://cdn.magicpatterns.com/uploads/d4QE6zV4kunC3S4gRbh3SC/HR_Page.png',
    features: [
      'Culture Intelligence Dashboard',
      'Anonymous AI Feedback Engine',
      'Automated Pulse Surveys',
      'Retention & Burnout Prediction',
      'AI Action Recommendations',
      'Workforce KPI Tracking',
      'HR AI Assistant',
      'Executive Insights Reporting',
    ],
    problemsSolved: [
      'Scaling culture without losing transparency',
      'Supporting employees without expanding HR headcount',
      'Detecting burnout and attrition risks early',
      'Acting on insights instead of just collecting data',
      'Turning qualitative feedback into measurable KPIs',
    ],
    targetAudience: [
      'HR Directors',
      'People Operations Managers',
      'CEOs focused on culture',
      'Team Leads',
    ],
    faqs: [
      {
        question: 'Are the surveys truly anonymous?',
        answer:
          'Yes, we use a strict anonymity threshold. Individual responses are never linked to specific employees in reports visible to management.',
      },
      {
        question: 'How often are surveys sent?',
        answer:
          'You can customize the frequency. Most clients choose weekly or bi-weekly micro-surveys that take less than 30 seconds to complete.',
      },
      {
        question: 'Can we customize the questions?',
        answer:
          'Yes, while we provide a scientifically validated question bank, you can add custom questions to track specific initiatives.',
      },
      {
        question: 'Does it work for remote teams?',
        answer:
          'Cooler Pulse is designed with remote and hybrid teams in mind, helping you maintain connection and visibility without physical presence.',
      },
    ],
  },
  {
    id: 'brandably',
    name: 'Brandably',
    tagline: 'Reputation management & brand monitoring.',
    description:
      'Monitor your brand presence across the web, social media, and review sites. Get alerted to mentions instantly and manage your online reputation from a single dashboard.',
    icon: Star,
    color: 'amber',
    features: [
      'Real-time mention alerts',
      'Sentiment analysis AI',
      'Review aggregation from 50+ sites',
      'Competitor brand monitoring',
      'Social media listening',
      'Crisis detection alerts',
      'Automated reporting',
      'Influencer identification',
    ],
    problemsSolved: [
      'Missing critical customer feedback',
      'Slow response times to negative reviews',
      'Difficulty tracking brand sentiment over time',
      'Fragmented social media monitoring',
      'Unnoticed PR crises',
    ],
    targetAudience: [
      'Marketing Managers',
      'PR Professionals',
      'Brand Managers',
      'Customer Success Leads',
    ],
    faqs: [
      {
        question: 'Which platforms do you monitor?',
        answer:
          'We monitor Twitter, Facebook, Instagram, LinkedIn, Reddit, major news sites, blogs, forums, and review sites like G2, Capterra, and Google Reviews.',
      },
      {
        question: 'How accurate is the sentiment analysis?',
        answer:
          'Our proprietary NLP model achieves 94% accuracy in sentiment classification, understanding context, sarcasm, and industry-specific terms.',
      },
      {
        question: 'Can I reply to reviews directly?',
        answer:
          'Yes, for supported platforms like Google Reviews and Facebook, you can reply directly from the Brandably dashboard.',
      },
      {
        question: 'Is there a limit on keywords?',
        answer:
          'Limits depend on your plan, but our Enterprise tier offers unlimited keyword and competitor tracking.',
      },
    ],
  },
  {
    id: 'price-monitor',
    name: 'Price Monitor',
    tagline: 'Competitive pricing intelligence.',
    description:
      'Stay ahead of the market with automated competitor price tracking. Get insights on pricing strategies, discount patterns, and stock levels to optimize your own pricing.',
    icon: TrendingUp,
    color: 'emerald',
    features: [
      '24/7 competitor price tracking',
      'Dynamic pricing recommendations',
      'Stock availability monitoring',
      'Promotional activity alerts',
      'Historical pricing trends',
      'MAP compliance monitoring',
      'Global currency support',
      'API access for dynamic pricing',
    ],
    problemsSolved: [
      'Losing sales due to uncompetitive pricing',
      'Manual price checking taking too much time',
      'Leaving money on the table with underpricing',
      'Missing competitor promotions',
      'Inconsistent pricing across channels',
    ],
    targetAudience: [
      'E-commerce Managers',
      'Pricing Analysts',
      'Sales Directors',
      'Retail Buyers',
    ],
    faqs: [
      {
        question: 'How frequently is data updated?',
        answer:
          'Standard plans update daily. Enterprise plans can be configured for hourly or even real-time updates for high-velocity markets.',
      },
      {
        question: 'Can you track prices behind logins?',
        answer:
          'Yes, our advanced crawlers can navigate complex sites, including those requiring login, location selection, or adding items to cart.',
      },
      {
        question: 'Do you offer dynamic repricing?',
        answer:
          'We provide API endpoints that your e-commerce platform can query to adjust prices automatically based on your defined rules.',
      },
      {
        question: 'Is it legal to scrape competitor prices?',
        answer:
          'Yes, monitoring public pricing data is legal in most jurisdictions. We adhere to strict ethical crawling standards to not impact target site performance.',
      },
    ],
  },
  {
    id: 'legal-help',
    name: 'Legal Help',
    tagline: 'Legal automation & compliance tools.',
    description:
      'Streamline your legal operations with automated contract generation, compliance tracking, and document management. Reduce legal costs and ensure your business stays compliant.',
    icon: Scale,
    color: 'violet',
    features: [
      'Automated contract templates',
      'E-signature integration',
      'Compliance deadline tracking',
      'GDPR & CCPA management tools',
      'Legal document repository',
      'Risk assessment workflows',
      'External counsel collaboration',
      'Audit trail generation',
    ],
    problemsSolved: [
      'High legal fees for standard documents',
      'Lost or disorganized contracts',
      'Missed compliance deadlines',
      'Slow contract turnaround times',
      'Inconsistent legal terms across the business',
    ],
    targetAudience: [
      'General Counsels',
      'Operations Managers',
      'Compliance Officers',
      'Founders',
    ],
    faqs: [
      {
        question: 'Are the templates legally binding?',
        answer:
          'Yes, our templates are drafted by top-tier law firms and are regularly updated to ensure compliance with current regulations.',
      },
      {
        question: 'Does it replace a lawyer?',
        answer:
          'Legal Help handles routine legal work and compliance, freeing up your legal team or external counsel to focus on complex strategic matters.',
      },
      {
        question: 'Is data stored in my jurisdiction?',
        answer:
          'We offer data residency options in the US, EU, UK, and APAC regions to meet your specific compliance requirements.',
      },
      {
        question: 'Can I upload my own templates?',
        answer:
          'Yes, you can turn your existing contracts into smart, automated templates with variable fields and conditional logic.',
      },
    ],
  },
  {
    id: 'papershredder',
    name: 'PaperShredder',
    tagline: 'Document processing & digitization.',
    description:
      'Transform physical documents into searchable digital assets. Use OCR and AI to extract data from invoices, receipts, and forms automatically, eliminating manual data entry.',
    icon: FileText,
    color: 'rose',
    features: [
      'AI-powered OCR extraction',
      'Bulk document scanning',
      'Automated data entry',
      'Searchable PDF conversion',
      'Workflow automation triggers',
      'Secure document archiving',
      'Mobile scanning app',
      'Metadata tagging',
    ],
    problemsSolved: [
      'Time-consuming manual data entry',
      'Physical storage costs and space',
      'Lost documents',
      'Inability to search within paper files',
      'Slow processing of invoices and forms',
    ],
    targetAudience: [
      'Office Managers',
      'Accounts Payable Teams',
      'Records Managers',
      'Administrative Staff',
    ],
    faqs: [
      {
        question: 'How accurate is the OCR?',
        answer:
          'Our AI-driven OCR achieves 99%+ accuracy on standard printed documents and constantly learns from corrections to improve over time.',
      },
      {
        question: 'What file formats are supported?',
        answer:
          'We support PDF, JPG, PNG, TIFF, and can export data to CSV, Excel, JSON, or directly to your ERP/CRM system.',
      },
      {
        question: 'Is it secure for sensitive documents?',
        answer:
          'Yes, PaperShredder is HIPAA and GDPR compliant, with role-based access controls and detailed audit logs for all document interactions.',
      },
      {
        question: 'Can it handle handwritten text?',
        answer:
          'Yes, our advanced engine can recognize and extract legible handwritten text from forms and notes with high accuracy.',
      },
    ],
  },
]
